import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { modelsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { CreateModelRequest, UpdateModelRequest, PaginationParams } from '@geely/shared';

interface UseModelsOptions {
  enabled?: boolean;
}

export function useModels(options: UseModelsOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.models,
    queryFn: async () => {
      const response = await modelsApi.list();
      return response.data;
    },
    enabled,
  });
}

export function useModelsPaginated(params: Required<PaginationParams>) {
  return useQuery({
    queryKey: queryKeys.modelsPaginated(params),
    queryFn: async () => {
      const response = await modelsApi.listPaginated(params);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useModel(id: string) {
  return useQuery({
    queryKey: queryKeys.model(id),
    queryFn: async () => {
      const response = await modelsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateModelRequest) => {
      const response = await modelsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}

export function useUpdateModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateModelRequest }) => {
      const response = await modelsApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}

export function useDeleteModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await modelsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}
